import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tablet, Sparkles } from "lucide-react";
import { claimPairing } from "@/lib/childDevice";
import { useSEO } from "@/lib/seo";

/**
 * Tablet-side: parent walks over with the 6-char code and types it in.
 * Once claimed, the device becomes a communication-only child tablet.
 * The parent unpairs from their admin device when finished.
 */
export default function PairChildDevice() {
  useSEO({
    title: "Pair this device as a child tablet",
    description: "Lock this tablet into communication-only mode.",
    path: "/pair-device",
  });
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length !== 6) {
      setError("Pairing codes are 6 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await claimPairing({ code: trimmed });
      setOk(true);
      const pid = r?.pairing?.default_profile_id;
      // Reload so AppRouter re-evaluates isChildDevice() and the new
      // locked shell takes over. Small delay so the parent sees the
      // confirmation copy.
      setTimeout(() => {
        if (pid) {
          window.location.href = `/board/${pid}`;
        } else {
          window.location.href = "/profiles";
        }
      }, 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Could not pair this device.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-8 w-full max-w-md bg-[color:var(--paper)]">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] mb-3">
            <Tablet size={28} aria-hidden />
          </div>
          <h1 className="text-2xl font-extrabold">Pair this tablet</h1>
          <p className="text-sm text-[color:var(--ink)]/70 mt-2">
            On your phone or laptop, open <strong>Admin → Child devices</strong>{" "}
            and tap <strong>Add a tablet</strong>. Type the 6-character code
            below.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            className="brutal-input w-full text-center text-3xl tracking-[0.4em] font-extrabold uppercase"
            autoComplete="off"
            autoCapitalize="characters"
            data-testid="pair-code-input"
          />
          {error && (
            <p
              className="text-sm text-red-600 text-center"
              data-testid="pair-error"
            >
              {error}
            </p>
          )}
          {ok && (
            <p
              className="text-sm text-emerald-700 text-center font-bold inline-flex items-center justify-center gap-2 w-full"
              data-testid="pair-success"
            >
              <Sparkles size={16} /> Paired. This tablet is now communication-only.
            </p>
          )}
          <button
            type="submit"
            className="pill-btn primary w-full justify-center text-lg py-3"
            disabled={busy || ok}
            data-testid="pair-submit"
          >
            {busy ? "Pairing…" : ok ? "Done!" : "Pair this device"}
          </button>
        </form>

        <div className="text-center mt-6 text-xs text-[color:var(--ink)]/60">
          <Link to="/profiles" className="underline">
            Cancel and return to profiles
          </Link>
        </div>
      </div>
    </div>
  );
}
